<template>
  <footer class="package-builder-footer">
    <div class="package-builder-footer__actions">
      <button
        type="button"
        class="btn btn-o package-builder-footer__cancel"
        :disabled="isSubmitting"
        @click="handleCancel"
      >
        {{ cancelLabel }}
      </button>
      <button
        type="button"
        class="btn btn-alt package-builder-footer__cta"
        :disabled="ctaDisabled"
        @click="handleSubmit"
      >
        <span
          v-if="isSubmitting"
          class="spinner-border spinner-border-sm me-2"
          aria-hidden="true"
        />
        {{ ctaLabel }}
      </button>
    </div>
  </footer>
</template>

<script lang="ts">
import { defineComponent } from 'vue'

import { useAppContentStore } from '@/stores/appContent'
import { useCartStore } from '@/stores/cart'
import { usePackageBuilderStore } from '@/stores/packageBuilder'
import { mapActions, mapState, mapStores } from 'pinia'

import { useCartMutationFeedback } from '@/composables/useCartMutationFeedback'

import {
  type BuilderContent,
  resolveBuilderContent,
  substitute
} from '@/components/Senior/PackageBuilder/content'

import { feedbackContent } from '@/content/feedback'

export default defineComponent({
  name: 'PackageBuilderFooter',
  setup() {
    return {
      cartMutationFeedback: useCartMutationFeedback()
    }
  },
  computed: {
    ...mapStores(useAppContentStore, useCartStore),
    ...mapState(usePackageBuilderStore, [
      'isSubmitting',
      'editCartBundleId',
      'isValidForCartBundle',
      'bundleNameForCartBundle'
    ]),
    content(): BuilderContent {
      return resolveBuilderContent(this.appContentStore.seniorPackageBuilderContent)
    },
    ctaDisabled(): boolean {
      return (
        !this.editCartBundleId ||
        !this.isValidForCartBundle(this.editCartBundleId) ||
        this.isSubmitting ||
        this.cartStore.mutationsBlocked
      )
    },
    cancelLabel(): string {
      return this.content.cancelLabel
    },
    ctaLabel(): string {
      return this.isSubmitting ? this.content.ctaSubmittingLabel : this.content.ctaLabel
    }
  },
  methods: {
    ...mapActions(usePackageBuilderStore, { closeStore: 'closeModal', submitStore: 'submit' }),
    handleCancel() {
      if (this.isSubmitting) return
      this.closeStore()
    },
    async handleSubmit() {
      const cartBundleId = this.editCartBundleId
      if (!cartBundleId) return
      // Capture the bundle name before submit() resolves and state is cleared.
      const bundleName = this.bundleNameForCartBundle(cartBundleId)
      try {
        await this.cartMutationFeedback.run({
          mutate: () => this.submitStore([cartBundleId]),
          onSuccess: () => this.closeStore(),
          successMessage: bundleName
            ? substitute(this.content.toastSuccessTemplate, {
                PackageBuilderBundleName: bundleName
              })
            : feedbackContent.unnamedItemAddedToCart,
          errorMessage: this.content.errorMessage
        })
      } catch {
        // The feedback wrapper already surfaced the error toast; keep the builder open rather
        // than letting the rejection escape the click handler.
      }
    }
  }
})
</script>

<style scoped lang="scss">
.package-builder-footer {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  padding-bottom: 1.5rem;
  border-top: 1px solid #eee;
  background: #fff;
  flex-shrink: 0;

  &__actions {
    display: flex;
    gap: 0.75rem;
  }

  &__cancel {
    flex: 0 0 auto;
  }

  &__cta {
    flex: 1 1 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  @include media-breakpoint-up(md) {
    padding: 1rem 2.5rem;

    &__actions {
      justify-content: flex-end;
    }

    &__cta {
      flex: 0 0 auto;
      min-width: 200px;
    }
  }
}
</style>
